(() => {
  const SELECTORS = {
    skeleton: '[data-vify-gift-card-skeleton]',
    price: '.v-gc-price',
    customAmountInput: '.v-gc-form-input[type="number"]',
    variantButton: '.v-gc-variant',
    activeVariant: '.v-gc-variant.v-primary-button',
    subImage: '.v-gc-sub-image',
    checkoutButton: 'button.v-gc-add-to-cart-button'
  };

  const initializedSkeletons = new WeakSet();

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  };

  const getGiftCardPriceElement = (skeleton) => {
    return document.querySelector(skeleton.dataset.vifyPriceSelector || SELECTORS.price);
  };

  const getCustomAmountInput = () => {
    return document.querySelector(SELECTORS.customAmountInput);
  };

  const isElementVisibleInViewport = (element) => {
    if (!element) {
      return false;
    }

    const styles = window.getComputedStyle(element);

    if (
      styles.display === 'none'
      || styles.visibility === 'hidden'
      || styles.opacity === '0'
      || element.offsetWidth === 0
      || element.offsetHeight === 0
    ) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return (
      rect.bottom > 0
      && rect.right > 0
      && rect.top < viewportHeight
      && rect.left < viewportWidth
    );
  };

  const isCustomVariantSelected = () => {
    const activeVariant = document.querySelector(SELECTORS.activeVariant);

    if (!activeVariant) {
      return false;
    }

    return (activeVariant.textContent || '').toLowerCase().includes('personalizzato');
  };

  const isAmountValid = () => {
    const customAmountInput = getCustomAmountInput();

    if (!customAmountInput) {
      return true;
    }

    const rawValue = customAmountInput.value.trim();

    if (!rawValue) {
      return false;
    }

    const parsedValue = parseFloat(rawValue.replace(',', '.'));

    if (Number.isNaN(parsedValue)) {
      return false;
    }

    const min = customAmountInput.min ? parseFloat(customAmountInput.min) : null;
    const max = customAmountInput.max ? parseFloat(customAmountInput.max) : null;

    if (min !== null && parsedValue < min) {
      return false;
    }

    if (max !== null && parsedValue > max) {
      return false;
    }

    return true;
  };

  const updateCheckoutState = () => {
    const useCustomAmount = isCustomVariantSelected();
    const validAmount = isAmountValid();
    const customAmountInput = getCustomAmountInput();
    const checkoutButtons = document.querySelectorAll(SELECTORS.checkoutButton);

    checkoutButtons.forEach((button) => {
      if (!useCustomAmount || validAmount) {
        button.disabled = false;
        button.classList.remove('gc-disabled');
        return;
      }

      button.disabled = true;
      button.classList.add('gc-disabled');
    });

    if (customAmountInput) {
      customAmountInput.classList.toggle('gc-invalid', useCustomAmount && !validAmount);
    }
  };

  const syncSelectedImage = () => {
    const amountButtons = document.querySelectorAll(SELECTORS.variantButton);
    const subImages = document.querySelectorAll(SELECTORS.subImage);

    if (!amountButtons.length || !subImages.length) {
      return;
    }

    const valueToImage = {};
    let customImage = null;

    subImages.forEach((image) => {
      const match = image.src.match(/Gift_(\d+)/);

      if (match) {
        valueToImage[parseInt(match[1], 10)] = image;
      }

      if (image.src.includes('Value_Gift.jpg')) {
        customImage = image;
      }
    });

    const selectImageForValue = (value) => {
      const image = valueToImage[value];

      if (image) {
        image.click();
      }
    };

    const selectCustomImage = () => {
      if (customImage) {
        customImage.click();
      }
    };

    amountButtons.forEach((button) => {
      if (button.dataset.giftImgBound === '1') {
        return;
      }

      button.dataset.giftImgBound = '1';
      button.addEventListener('click', () => {
        const text = (button.textContent || '').trim().toLowerCase();

        if (text.includes('personalizzato')) {
          selectCustomImage();
          window.setTimeout(updateCheckoutState, 100);
          return;
        }

        const valueMatch = text.match(/(\d+)[.,]?\d*/);

        if (valueMatch) {
          selectImageForValue(parseInt(valueMatch[1], 10));
        }

        window.setTimeout(updateCheckoutState, 100);
      });
    });

    const activeButton = document.querySelector(SELECTORS.activeVariant);

    if (!activeButton) {
      return;
    }

    const activeText = (activeButton.textContent || '').trim().toLowerCase();

    if (activeText.includes('personalizzato')) {
      selectCustomImage();
      return;
    }

    const activeValueMatch = activeText.match(/(\d+)[.,]?\d*/);

    if (activeValueMatch) {
      selectImageForValue(parseInt(activeValueMatch[1], 10));
    }
  };

  const bindCustomAmountValidation = () => {
    const customAmountInput = getCustomAmountInput();

    if (!customAmountInput || customAmountInput.dataset.gcValidationBound === '1') {
      return;
    }

    customAmountInput.dataset.gcValidationBound = '1';

    ['input', 'change', 'keyup'].forEach((eventName) => {
      customAmountInput.addEventListener(eventName, updateCheckoutState);
    });
  };

  const hideSkeleton = (state) => {
    if (!state.skeleton || state.skeleton.dataset.vifySkeletonHidden === 'true') {
      return;
    }

    state.skeleton.dataset.vifySkeletonHidden = 'true';
    state.skeleton.style.display = 'none';
    state.skeleton.hidden = true;
    state.skeleton.setAttribute('aria-hidden', 'true');

    if (state.mutationObserver) {
      state.mutationObserver.disconnect();
    }

    if (state.intersectionObserver) {
      state.intersectionObserver.disconnect();
    }

    if (state.checkInterval) {
      window.clearInterval(state.checkInterval);
    }
  };

  const applyEnhancements = (state) => {
    const priceElement = getGiftCardPriceElement(state.skeleton);

    if (!isElementVisibleInViewport(priceElement)) {
      return;
    }

    hideSkeleton(state);
    syncSelectedImage();
    bindCustomAmountValidation();
    window.setTimeout(updateCheckoutState, 100);
  };

  const observePriceVisibility = (state) => {
    const priceElement = getGiftCardPriceElement(state.skeleton);

    if (!priceElement || state.observedPriceElement === priceElement) {
      return;
    }

    state.observedPriceElement = priceElement;

    if (state.intersectionObserver) {
      state.intersectionObserver.disconnect();
    }

    state.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && isElementVisibleInViewport(entry.target)) {
          applyEnhancements(state);
        }
      });
    }, {
      threshold: 0.01
    });

    state.intersectionObserver.observe(priceElement);
  };

  const initSkeleton = (skeleton) => {
    if (initializedSkeletons.has(skeleton)) {
      return;
    }

    initializedSkeletons.add(skeleton);

    const state = {
      skeleton,
      mutationObserver: null,
      intersectionObserver: null,
      observedPriceElement: null,
      checkInterval: null
    };

    applyEnhancements(state);
    observePriceVisibility(state);

    state.mutationObserver = new MutationObserver(() => {
      observePriceVisibility(state);
      applyEnhancements(state);
    });

    if (document.body) {
      state.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    state.checkInterval = window.setInterval(() => {
      observePriceVisibility(state);
      applyEnhancements(state);
    }, 250);
  };

  const init = (root = document) => {
    root.querySelectorAll(SELECTORS.skeleton).forEach((skeleton) => {
      initSkeleton(skeleton);
    });
  };

  onReady(() => {
    init();
    document.addEventListener('shopify:section:load', (event) => {
      init(event.target);
    });
  });
})();
